/**
 * Centralized error message catalog (FR / EN / AR).
 * Merged into i18n resources at init — services keep stable codes via resolveServiceErrorKey().
 */

export const SERVICE_ERROR_CODES = {
  notAuthenticated: "errors.notAuthenticated",
  vehicleUnavailable: "errors.booking.vehicleUnavailable",
  datesLocked: "errors.booking.datesLocked",
  datesBlocked: "errors.booking.datesBlocked",
  rateLimited: "errors.booking.rateLimited",
  lockCreateFailed: "errors.booking.lockCreateFailed",
  lockReleaseFailed: "errors.booking.lockReleaseFailed",
  bookingCreateFailed: "errors.booking.createFailed",
  bookingNotFound: "errors.booking.notFound",
  bookingUnauthorized: "errors.booking.unauthorized",
  bookingAlreadyCancelled: "errors.booking.alreadyCancelled",
  bookingCannotCancelCompleted: "errors.booking.cannotCancelCompleted",
  bookingCannotCancelRejected: "errors.booking.cannotCancelRejected",
  bookingCancelUnauthorized: "errors.booking.cancelUnauthorized",
  bookingCancelFailed: "errors.booking.cancelFailed",
  ownerOnlyConfirm: "errors.booking.ownerOnlyConfirm",
  bookingNotPending: "errors.booking.notPending",
  ownerOnlyReject: "errors.booking.ownerOnlyReject",
  ownerOnlyCheckIn: "errors.booking.ownerOnlyCheckIn",
  renterOnlyCheckOut: "errors.booking.renterOnlyCheckOut",
  checkInRequired: "errors.booking.checkInRequired",
  generic: "errors.generic",
  network: "errors.network",
  timeout: "errors.timeout",
  permission: "errors.permission",
  notFound: "errors.notFound",
  server: "errors.server",
  validation: "errors.validation",
} as const;

/** Maps legacy FR/EN thrown messages to i18n keys */
export const LEGACY_ERROR_MESSAGE_TO_KEY: Record<string, string> = {
  "Not authenticated": SERVICE_ERROR_CODES.notAuthenticated,
  "Le véhicule n'est pas disponible pour ces dates": SERVICE_ERROR_CODES.vehicleUnavailable,
  "Ces dates sont déjà verrouillées par un autre utilisateur": SERVICE_ERROR_CODES.datesLocked,
  "Certaines dates sont bloquées par le propriétaire": SERVICE_ERROR_CODES.datesBlocked,
  "Trop de tentatives. Réessayez dans 15 minutes.": SERVICE_ERROR_CODES.rateLimited,
  "Impossible de créer le verrou de réservation": SERVICE_ERROR_CODES.lockCreateFailed,
  "Erreur lors de la création du verrou de réservation": SERVICE_ERROR_CODES.lockCreateFailed,
  "Impossible de libérer le verrou": SERVICE_ERROR_CODES.lockReleaseFailed,
  "Erreur lors de la libération du verrou": SERVICE_ERROR_CODES.lockReleaseFailed,
  "Aucun ID de réservation retourné": SERVICE_ERROR_CODES.bookingCreateFailed,
  "Impossible de récupérer la réservation créée": SERVICE_ERROR_CODES.bookingCreateFailed,
  "Erreur lors de la création de la réservation": SERVICE_ERROR_CODES.bookingCreateFailed,
  "Booking not found": SERVICE_ERROR_CODES.bookingNotFound,
  "Unauthorized: Not your booking": SERVICE_ERROR_CODES.bookingUnauthorized,
  "Cette réservation est déjà annulée": SERVICE_ERROR_CODES.bookingAlreadyCancelled,
  "Impossible d'annuler une réservation terminée": SERVICE_ERROR_CODES.bookingCannotCancelCompleted,
  "Impossible d'annuler une réservation déjà refusée": SERVICE_ERROR_CODES.bookingCannotCancelRejected,
  "Vous n'êtes pas autorisé à annuler cette réservation": SERVICE_ERROR_CODES.bookingCancelUnauthorized,
  "Réservation introuvable": SERVICE_ERROR_CODES.bookingNotFound,
  "Erreur lors de l'annulation de la réservation": SERVICE_ERROR_CODES.bookingCancelFailed,
  "Unauthorized: Only owner can confirm": SERVICE_ERROR_CODES.ownerOnlyConfirm,
  "Booking is not pending": SERVICE_ERROR_CODES.bookingNotPending,
  "Unauthorized: Only owner can reject": SERVICE_ERROR_CODES.ownerOnlyReject,
  "Only pending bookings can be rejected": SERVICE_ERROR_CODES.bookingNotPending,
  "Unauthorized: Only owner can check-in": SERVICE_ERROR_CODES.ownerOnlyCheckIn,
  "Booking must be confirmed to check-in": SERVICE_ERROR_CODES.bookingNotPending,
  "Unauthorized: Only renter can check-out": SERVICE_ERROR_CODES.renterOnlyCheckOut,
  "Must check-in before check-out": SERVICE_ERROR_CODES.checkInRequired,
};

export const ERROR_CATALOG_FR: Record<string, unknown> = {
  errors: {
    generic: "Une erreur est survenue. Veuillez réessayer.",
    notAuthenticated: "Vous devez être connecté pour continuer.",
    network: "Erreur de connexion. Vérifiez votre internet et réessayez.",
    timeout: "La requête a pris trop de temps. Veuillez réessayer.",
    permission: "Vous n'avez pas la permission d'effectuer cette action.",
    notFound: "La ressource demandée est introuvable.",
    server: "Erreur serveur. Réessayez dans quelques instants.",
    validation: "Les informations saisies ne sont pas valides.",
    validationStartDateFuture: "La date de début doit être dans le futur.",
    validationEndDateAfterStart: "La date de fin doit être après la date de début.",
    retry: "Réessayer",
    auth: {
      emailNotVerified:
        "Votre email n'est pas encore vérifié. Consultez votre boîte de réception et cliquez sur le lien de vérification.",
      weakPassword:
        "Le mot de passe est trop faible. Utilisez au moins 8 caractères, une majuscule, une minuscule et un chiffre.",
    },
    booking: {
      vehicleUnavailable: "Ce véhicule n'est pas disponible pour ces dates.",
      datesLocked: "Ces dates sont déjà réservées par un autre utilisateur.",
      datesBlocked: "Certaines dates sont indisponibles pour ce véhicule.",
      rateLimited: "Trop de tentatives. Réessayez dans 15 minutes.",
      lockCreateFailed: "Impossible de verrouiller ces dates. Réessayez.",
      lockReleaseFailed: "Impossible de libérer le verrou. Réessayez.",
      createFailed: "Impossible de créer la réservation. Réessayez.",
      notFound: "Réservation introuvable.",
      unauthorized: "Vous n'avez pas accès à cette réservation.",
      alreadyCancelled: "Cette réservation est déjà annulée.",
      cannotCancelCompleted: "Impossible d'annuler une réservation terminée.",
      cannotCancelRejected: "Impossible d'annuler une réservation refusée.",
      cancelUnauthorized: "Vous n'êtes pas autorisé à annuler cette réservation.",
      cancelFailed: "Impossible d'annuler la réservation. Réessayez.",
      ownerOnlyConfirm: "Seul le propriétaire peut confirmer cette réservation.",
      notPending: "Cette réservation n'est plus en attente.",
      ownerOnlyReject: "Seul le propriétaire peut refuser cette réservation.",
      ownerOnlyCheckIn: "Seul le propriétaire peut enregistrer la prise en charge.",
      renterOnlyCheckOut: "Seul le locataire peut enregistrer le retour.",
      checkInRequired: "La prise en charge doit être enregistrée avant le retour.",
    },
  },
  bookingErrors: {
    VEHICLE_NOT_AVAILABLE: {
      title: "Véhicule non disponible",
      description: "Ce véhicule n'est plus disponible pour ces dates. Essayez d'autres dates ou un autre véhicule.",
      actionLabel: "Changer les dates",
    },
    LOCK_EXPIRED: {
      title: "Réservation expirée",
      description: "Votre réservation a expiré. Veuillez resélectionner les dates et continuer.",
      actionLabel: "Resélectionner les dates",
    },
    LOCK_INVALID_OR_EXPIRED: {
      title: "Réservation expirée",
      description: "Votre réservation a expiré. Veuillez resélectionner les dates et continuer.",
      actionLabel: "Resélectionner les dates",
    },
    LOCK_ID_REQUIRED: {
      title: "Erreur de réservation",
      description: "Une erreur s'est produite lors de la préparation de votre réservation. Veuillez réessayer.",
      actionLabel: "Réessayer",
    },
    PRICE_MISMATCH: {
      title: "Tarif mis à jour",
      description: "Le tarif a changé. Veuillez rafraîchir la page et réessayer.",
      actionLabel: "Rafraîchir la page",
    },
    PAYMENT_FAILED: {
      title: "Paiement échoué",
      description: "Votre paiement n'a pas pu être traité. Vérifiez vos informations de carte et réessayez.",
      actionLabel: "Réessayer",
    },
    PAYMENT_DECLINED: {
      title: "Paiement refusé",
      description: "Votre banque a refusé le paiement. Contactez votre banque ou utilisez une autre carte.",
      actionLabel: "Réessayer avec une autre carte",
    },
    INSUFFICIENT_FUNDS: {
      title: "Fonds insuffisants",
      description: "Votre compte ne dispose pas de fonds suffisants pour cette transaction.",
      actionLabel: "Utiliser une autre carte",
    },
    DUPLICATE_BOOKING: {
      title: "Réservation déjà existante",
      description: "Une réservation existe déjà pour ces dates. Veuillez choisir d'autres dates.",
      actionLabel: "Changer les dates",
    },
    BOOKING_CREATION_FAILED: {
      title: "Erreur de réservation",
      description: "Nous n'avons pas pu créer votre réservation. Votre paiement sera automatiquement remboursé.",
      actionLabel: "Réessayer",
    },
    NETWORK_ERROR: {
      title: "Problème de connexion",
      description: "Une erreur de connexion est survenue. Vérifiez votre connexion internet et réessayez.",
      actionLabel: "Réessayer",
    },
    TIMEOUT: {
      title: "Délai d'attente dépassé",
      description: "La requête a pris trop de temps. Veuillez réessayer.",
      actionLabel: "Réessayer",
    },
    SERVER_ERROR: {
      title: "Erreur serveur",
      description: "Une erreur serveur est survenue. Veuillez réessayer dans quelques instants.",
      actionLabel: "Réessayer",
    },
    VALIDATION_ERROR: {
      title: "Données invalides",
      description: "Les informations fournies ne sont pas valides. Veuillez vérifier et réessayer.",
      actionLabel: "Vérifier les informations",
    },
    MISSING_REQUIRED_FIELDS: {
      title: "Informations manquantes",
      description: "Certaines informations requises sont manquantes. Veuillez compléter tous les champs.",
      actionLabel: "Compléter le formulaire",
    },
    UNKNOWN_ERROR: {
      title: "Une erreur est survenue",
      description: "Une erreur inattendue s'est produite. Veuillez réessayer ou contacter le support si le problème persiste.",
      actionLabel: "Contacter le support",
    },
  },
};

export const ERROR_CATALOG_EN: Record<string, unknown> = {
  errors: {
    generic: "Something went wrong. Please try again.",
    notAuthenticated: "You must be signed in to continue.",
    network: "Connection error. Check your internet and try again.",
    timeout: "The request timed out. Please try again.",
    permission: "You don't have permission to perform this action.",
    notFound: "The requested resource was not found.",
    server: "Server error. Please try again shortly.",
    validation: "The information provided is not valid.",
    validationStartDateFuture: "The start date must be in the future.",
    validationEndDateAfterStart: "The end date must be after the start date.",
    retry: "Try again",
    auth: {
      emailNotVerified:
        "Your email is not verified yet. Check your inbox and click the verification link.",
      weakPassword:
        "This password is too weak. Use at least 8 characters, one uppercase letter, one lowercase letter and one digit.",
    },
    booking: {
      vehicleUnavailable: "This vehicle is not available for these dates.",
      datesLocked: "These dates are already held by another user.",
      datesBlocked: "Some dates are unavailable for this vehicle.",
      rateLimited: "Too many attempts. Try again in 15 minutes.",
      lockCreateFailed: "Could not hold these dates. Please try again.",
      lockReleaseFailed: "Could not release the hold. Please try again.",
      createFailed: "Could not create the booking. Please try again.",
      notFound: "Booking not found.",
      unauthorized: "You don't have access to this booking.",
      alreadyCancelled: "This booking is already cancelled.",
      cannotCancelCompleted: "Cannot cancel a completed booking.",
      cannotCancelRejected: "Cannot cancel a rejected booking.",
      cancelUnauthorized: "You are not allowed to cancel this booking.",
      cancelFailed: "Could not cancel the booking. Please try again.",
      ownerOnlyConfirm: "Only the owner can confirm this booking.",
      notPending: "This booking is no longer pending.",
      ownerOnlyReject: "Only the owner can reject this booking.",
      ownerOnlyCheckIn: "Only the owner can record check-in.",
      renterOnlyCheckOut: "Only the renter can record check-out.",
      checkInRequired: "Check-in must be recorded before check-out.",
    },
  },
  bookingErrors: {
    VEHICLE_NOT_AVAILABLE: {
      title: "Vehicle unavailable",
      description: "This vehicle is no longer available for these dates. Try other dates or another vehicle.",
      actionLabel: "Change dates",
    },
    LOCK_EXPIRED: {
      title: "Booking expired",
      description: "Your hold has expired. Please select dates again and continue.",
      actionLabel: "Select dates again",
    },
    LOCK_INVALID_OR_EXPIRED: {
      title: "Booking expired",
      description: "Your hold has expired. Please select dates again and continue.",
      actionLabel: "Select dates again",
    },
    LOCK_ID_REQUIRED: {
      title: "Booking error",
      description: "Something went wrong while preparing your booking. Please try again.",
      actionLabel: "Try again",
    },
    PRICE_MISMATCH: {
      title: "Price updated",
      description: "The price has changed. Please refresh the page and try again.",
      actionLabel: "Refresh page",
    },
    PAYMENT_FAILED: {
      title: "Payment failed",
      description: "Your payment could not be processed. Check your card details and try again.",
      actionLabel: "Try again",
    },
    PAYMENT_DECLINED: {
      title: "Payment declined",
      description: "Your bank declined the payment. Contact your bank or use another card.",
      actionLabel: "Try another card",
    },
    INSUFFICIENT_FUNDS: {
      title: "Insufficient funds",
      description: "Your account does not have enough funds for this transaction.",
      actionLabel: "Use another card",
    },
    DUPLICATE_BOOKING: {
      title: "Booking already exists",
      description: "A booking already exists for these dates. Please choose different dates.",
      actionLabel: "Change dates",
    },
    BOOKING_CREATION_FAILED: {
      title: "Booking error",
      description: "We could not create your booking. Your payment will be refunded automatically.",
      actionLabel: "Try again",
    },
    NETWORK_ERROR: {
      title: "Connection problem",
      description: "A connection error occurred. Check your internet and try again.",
      actionLabel: "Try again",
    },
    TIMEOUT: {
      title: "Request timed out",
      description: "The request took too long. Please try again.",
      actionLabel: "Try again",
    },
    SERVER_ERROR: {
      title: "Server error",
      description: "A server error occurred. Please try again in a few moments.",
      actionLabel: "Try again",
    },
    VALIDATION_ERROR: {
      title: "Invalid data",
      description: "The information provided is not valid. Please check and try again.",
      actionLabel: "Review information",
    },
    MISSING_REQUIRED_FIELDS: {
      title: "Missing information",
      description: "Some required information is missing. Please complete all fields.",
      actionLabel: "Complete form",
    },
    UNKNOWN_ERROR: {
      title: "Something went wrong",
      description: "An unexpected error occurred. Please try again or contact support if it persists.",
      actionLabel: "Contact support",
    },
  },
};

export const ERROR_CATALOG_AR: Record<string, unknown> = {
  errors: {
    generic: "حدث خطأ. يرجى المحاولة مرة أخرى.",
    notAuthenticated: "يجب تسجيل الدخول للمتابعة.",
    network: "مشكلة في الاتصال. تحقق من الإنترنت وحاول مجدداً.",
    timeout: "انتهت مهلة الطلب. يرجى المحاولة مرة أخرى.",
    permission: "ليس لديك صلاحية لتنفيذ هذا الإجراء.",
    notFound: "المورد المطلوب غير موجود.",
    server: "خطأ في الخادم. حاول بعد قليل.",
    validation: "البيانات المدخلة غير صالحة.",
    validationStartDateFuture: "يجب أن يكون تاريخ البداية في المستقبل.",
    validationEndDateAfterStart: "يجب أن يكون تاريخ النهاية بعد تاريخ البداية.",
    retry: "إعادة المحاولة",
    auth: {
      emailNotVerified:
        "لم يتم التحقق من بريدك الإلكتروني بعد. تحقق من صندوق الوارد واضغط على رابط التحقق.",
      weakPassword:
        "كلمة المرور ضعيفة جداً. استخدم 8 أحرف على الأقل مع حرف كبير وحرف صغير ورقم.",
    },
    booking: {
      vehicleUnavailable: "المركبة غير متاحة في هذه التواريخ.",
      datesLocked: "هذه التواريخ محجوزة مسبقاً من مستخدم آخر.",
      datesBlocked: "بعض التواريخ غير متاحة لهذه المركبة.",
      rateLimited: "محاولات كثيرة. أعد المحاولة بعد 15 دقيقة.",
      lockCreateFailed: "تعذّر حجز هذه التواريخ مؤقتاً. حاول مجدداً.",
      lockReleaseFailed: "تعذّر إلغاء الحجز المؤقت. حاول مجدداً.",
      createFailed: "تعذّر إنشاء الحجز. حاول مجدداً.",
      notFound: "الحجز غير موجود.",
      unauthorized: "ليس لديك صلاحية الوصول إلى هذا الحجز.",
      alreadyCancelled: "هذا الحجز ملغى مسبقاً.",
      cannotCancelCompleted: "لا يمكن إلغاء حجز مكتمل.",
      cannotCancelRejected: "لا يمكن إلغاء حجز مرفوض.",
      cancelUnauthorized: "غير مصرّح لك بإلغاء هذا الحجز.",
      cancelFailed: "تعذّر إلغاء الحجز. حاول مجدداً.",
      ownerOnlyConfirm: "التأكيد متاح للوكالة/المالك فقط.",
      notPending: "هذا الحجز لم يعد قيد الانتظار.",
      ownerOnlyReject: "الرفض متاح للوكالة/المالك فقط.",
      ownerOnlyCheckIn: "تسجيل الاستلام متاح للوكالة/المالك فقط.",
      renterOnlyCheckOut: "تسجيل الإرجاع متاح للمستأجر فقط.",
      checkInRequired: "يجب تسجيل الاستلام قبل الإرجاع.",
    },
  },
  bookingErrors: {
    VEHICLE_NOT_AVAILABLE: {
      title: "المركبة غير متاحة",
      description: "المركبة لم تعد متاحة في هذه التواريخ. جرّب تواريخاً أخرى أو مركبة مختلفة.",
      actionLabel: "تغيير التواريخ",
    },
    LOCK_EXPIRED: {
      title: "انتهت صلاحية الحجز",
      description: "انتهت صلاحية حجزك المؤقت. يُرجى إعادة اختيار التواريخ والمتابعة.",
      actionLabel: "إعادة اختيار التواريخ",
    },
    LOCK_INVALID_OR_EXPIRED: {
      title: "انتهت صلاحية الحجز",
      description: "انتهت صلاحية حجزك المؤقت. يُرجى إعادة اختيار التواريخ والمتابعة.",
      actionLabel: "إعادة اختيار التواريخ",
    },
    LOCK_ID_REQUIRED: {
      title: "خطأ في الحجز",
      description: "حدث خطأ أثناء تجهيز حجزك. يرجى المحاولة مرة أخرى.",
      actionLabel: "إعادة المحاولة",
    },
    PRICE_MISMATCH: {
      title: "تم تحديث السعر",
      description: "تغيّر السعر. يُرجى تحديث الصفحة والمحاولة مجدداً.",
      actionLabel: "تحديث الصفحة",
    },
    PAYMENT_FAILED: {
      title: "فشل الدفع",
      description: "تعذّر معالجة دفعتك. تحقق من بيانات البطاقة وحاول مجدداً.",
      actionLabel: "إعادة المحاولة",
    },
    PAYMENT_DECLINED: {
      title: "تم رفض الدفع",
      description: "رفض البنك العملية. تواصل مع بنكك أو استخدم بطاقة أخرى.",
      actionLabel: "تجربة بطاقة أخرى",
    },
    INSUFFICIENT_FUNDS: {
      title: "رصيد غير كافٍ",
      description: "لا يتوفر رصيد كافٍ لإتمام هذه العملية.",
      actionLabel: "استخدام بطاقة أخرى",
    },
    DUPLICATE_BOOKING: {
      title: "حجز موجود مسبقاً",
      description: "يوجد حجز بالفعل لهذه التواريخ. اختر تواريخاً مختلفة.",
      actionLabel: "تغيير التواريخ",
    },
    BOOKING_CREATION_FAILED: {
      title: "خطأ في الحجز",
      description: "تعذّر إنشاء حجزك. سيتم استرداد الدفع تلقائياً.",
      actionLabel: "إعادة المحاولة",
    },
    NETWORK_ERROR: {
      title: "مشكلة في الاتصال",
      description: "حدث خطأ في الاتصال. تحقق من الإنترنت وحاول مجدداً.",
      actionLabel: "إعادة المحاولة",
    },
    TIMEOUT: {
      title: "انتهت المهلة",
      description: "استغرق الطلب وقتاً طويلاً. يرجى المحاولة مرة أخرى.",
      actionLabel: "إعادة المحاولة",
    },
    SERVER_ERROR: {
      title: "خطأ في الخادم",
      description: "حدث خطأ في الخادم. حاول بعد قليل.",
      actionLabel: "إعادة المحاولة",
    },
    VALIDATION_ERROR: {
      title: "بيانات غير صالحة",
      description: "المعلومات المدخلة غير صالحة. تحقق منها وحاول مجدداً.",
      actionLabel: "مراجعة المعلومات",
    },
    MISSING_REQUIRED_FIELDS: {
      title: "معلومات ناقصة",
      description: "بعض الحقول المطلوبة غير مكتملة. أكمل جميع الحقول.",
      actionLabel: "إكمال النموذج",
    },
    UNKNOWN_ERROR: {
      title: "حدث خطأ",
      description: "حدث خطأ غير متوقع. حاول مجدداً أو تواصل مع الدعم إذا استمرت المشكلة.",
      actionLabel: "التواصل مع الدعم",
    },
  },
};

export const resolveServiceErrorKey = (message: string): string | null =>
  LEGACY_ERROR_MESSAGE_TO_KEY[message] ?? null;
